import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import AdCard from "@/components/AdCard";
import DisplayCampaignCard from "@/components/DisplayCampaignCard";
import { videoAds, displayCampaigns } from "@/data/reel";
import { ArrowRight, Film, Plus } from "lucide-react";
import { Link } from "react-router-dom";

const ALL = "All";
const PAGE_SIZE_VIDEOS = 12;
const PAGE_SIZE_DISPLAY = 6;

// Group-filter prefix. Encoded into the `industry` state when a quick-pick
// group header is clicked (or seeded via `?vertical=` query param).
const GROUP_PREFIX = "__group:";

const VERTICAL_TO_GROUP = {
  "home-services": "Home Services",
  tourism: "Tourism · Events · F&B",
};

const SampleAds = () => {
  const location = useLocation();
  const [industry, setIndustry] = useState(ALL);
  const [format, setFormat] = useState(ALL);
  // Pagination — visible counts. Reset whenever a filter changes.
  const [videoLimit, setVideoLimit] = useState(PAGE_SIZE_VIDEOS);
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE_DISPLAY);

  // Seed the initial industry from a `?vertical=home-services|tourism` query
  // string so visitors arriving from a hub page land pre-filtered to their
  // relevant ad work. We do this in an effect so the value can change if the
  // URL updates (e.g. soft-navigation between hubs).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vertical = params.get("vertical");
    if (vertical && VERTICAL_TO_GROUP[vertical]) {
      setIndustry(GROUP_PREFIX + VERTICAL_TO_GROUP[vertical]);
    }
  }, [location.search]);

  // Reset pagination on any filter change.
  useEffect(() => {
    setVideoLimit(PAGE_SIZE_VIDEOS);
    setDisplayLimit(PAGE_SIZE_DISPLAY);
  }, [industry, format]);

  // Helper — returns all industries an entry belongs to (supports optional `industries` array for multi-tag).
  // Empty / falsy industry strings are intentionally skipped so they don't create a blank filter chip.
  const entryIndustries = (e) => {
    const raw =
      Array.isArray(e.industries) && e.industries.length > 0
        ? e.industries
        : [e.industry];
    return raw.filter((i) => typeof i === "string" && i.trim().length > 0);
  };

  const industries = useMemo(() => {
    const set = new Set();
    videoAds.forEach((a) => entryIndustries(a).forEach((i) => set.add(i)));
    displayCampaigns.forEach((c) => entryIndustries(c).forEach((i) => set.add(i)));
    // Alphabetize, case-insensitive. "All" pinned first.
    const sorted = Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    return [ALL, ...sorted];
  }, []);

  // Visual grouping for the industry filter row. Industries not listed here
  // fall into "Other" automatically so newly added clients don't disappear.
  const INDUSTRY_GROUPS = [
    {
      label: "Home Services",
      members: [
        "HVAC",
        "Roofing",
        "Landscaping / Lawn & Garden",
        "Home Remodeling and Building Supply",
        "Real Estate / Developer / Home Builder",
        "Misc Home Services",
      ],
    },
    {
      label: "Tourism · Events · F&B",
      members: [
        "Tourism DMO",
        "Winery / Brewery / Cidery / Venue",
        "Luxury Travel",
        "Event",
        "Restaurant / Food & Beverage",
      ],
    },
    {
      label: "Other",
      // Filled with leftovers below.
      members: [],
    },
  ];
  const groupedIndustries = useMemo(() => {
    const taken = new Set(
      INDUSTRY_GROUPS.flatMap((g) => g.members).map((m) => m.toLowerCase())
    );
    const leftovers = industries
      .filter((i) => i !== ALL)
      .filter((i) => !taken.has(i.toLowerCase()))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return INDUSTRY_GROUPS.map((g) =>
      g.label === "Other"
        ? { ...g, members: leftovers }
        : {
            ...g,
            members: g.members.filter((m) =>
              industries.some((i) => i.toLowerCase() === m.toLowerCase())
            ),
          }
    ).filter((g) => g.members.length > 0);
  }, [industries]);

  const formats = [
    { key: ALL, label: "All Formats" },
    { key: "tv-social", label: "TV / Video Commercials" },
    { key: "reel", label: "Reels (Vertical)" },
    { key: "social-carousel", label: "Facebook Carousel" },
    { key: "mobile-display", label: "Mobile Display Ads" },
  ];

  const showVideos =
    format === ALL ||
    format === "tv-social" ||
    format === "reel" ||
    format === "social-static" ||
    format === "social-carousel";
  const showDisplay = format === ALL || format === "mobile-display";

  // Match by individual industry OR by selected group. Group selection is
  // encoded as `__group:<Group Label>` so we don't need a parallel state slot.
  const matchesIndustry = (e) => {
    if (industry === ALL) return true;
    if (industry.startsWith(GROUP_PREFIX)) {
      const groupLabel = industry.slice(GROUP_PREFIX.length);
      const group = groupedIndustries.find((g) => g.label === groupLabel);
      if (!group) return true;
      const members = new Set(group.members.map((m) => m.toLowerCase()));
      return entryIndustries(e).some((i) => members.has(i.toLowerCase()));
    }
    return entryIndustries(e).includes(industry);
  };

  const filteredVideos = videoAds
    .filter(
      (a) =>
        showVideos &&
        matchesIndustry(a) &&
        (format === ALL || a.format === format)
    )
    // Sort: carousel first (showcase piece), then TV/Social, Reels, static social, others.
    .sort((a, b) => {
      const rank = (f) =>
        f === "social-carousel"
          ? 0
          : f === "tv-social"
          ? 1
          : f === "reel"
          ? 2
          : f === "social-static"
          ? 3
          : 4;
      return rank(a.format) - rank(b.format);
    });

  const filteredCampaigns = displayCampaigns.filter(
    (c) => showDisplay && matchesIndustry(c)
  );

  const isEmpty =
    filteredVideos.length === 0 && filteredCampaigns.length === 0;

  // Paginated slices.
  const visibleVideos = filteredVideos.slice(0, videoLimit);
  const visibleCampaigns = filteredCampaigns.slice(0, displayLimit);
  const remainingVideos = filteredVideos.length - visibleVideos.length;
  const remainingCampaigns = filteredCampaigns.length - visibleCampaigns.length;

  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="sample-ads-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-12 sm:pt-14 sm:pb-20">
          <div className="flex items-center gap-3 mb-4 sm:mb-5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center">
              <Film size={20} className="text-[var(--af-lime)]" />
            </div>
            <div className="eyebrow">Sample Ads</div>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">
            Sample Ads <span className="text-[var(--af-lime)]">in your industry.</span>
          </h1>
          <p className="text-base sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mt-5 sm:mt-7 leading-relaxed">
            Real campaigns. Real clients. TV commercials, social media spots,
            vertical Reels, and mobile display ads, produced and placed by our
            boutique team.
          </p>
          <div
            className="mt-5 sm:mt-7 inline-flex items-center gap-3 px-4 py-2.5 rounded-full border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60"
            data-testid="sample-ads-vifl-badge"
          >
            <img
              src="/media/clients/vifl-horizontal.png"
              alt="Virginia Is For Lovers"
              className="h-5 w-auto opacity-95 flex-shrink-0"
            />
            <div className="text-[11px] uppercase tracking-widest text-[var(--af-cream)]/80 font-medium">
              Tourism work · VIFL-compliant · VTC grant-aligned
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section
        className="px-5 sm:px-8 py-6 sm:py-10 border-t border-b border-[var(--af-line)]"
        data-testid="sample-ads-filters"
      >
        <div className="max-w-7xl mx-auto flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.18em] text-[var(--af-cream)]/60">
                Industry:
              </span>
              <button
                onClick={() => setIndustry(ALL)}
                data-testid="filter-industry-all"
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded-full border transition-colors ${
                  industry === ALL
                    ? "bg-[var(--af-lime)] text-[var(--af-navy)] border-[var(--af-lime)] font-semibold"
                    : "border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 hover:border-[var(--af-lime)]"
                }`}
              >
                All
              </button>
            </div>
            {groupedIndustries.map((group) => {
              const groupValue = GROUP_PREFIX + group.label;
              const groupActive = industry === groupValue;
              return (
                <div
                  key={group.label}
                  className="flex flex-wrap items-baseline gap-2"
                  data-testid={`industry-group-${group.label.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "-")}`}
                >
                  <button
                    onClick={() => setIndustry(groupValue)}
                    data-testid={`filter-industry-group-${group.label.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "-")}`}
                    className={`text-[10px] uppercase tracking-[0.18em] font-semibold w-full sm:w-auto sm:min-w-[140px] text-left px-2 py-1 -ml-2 rounded transition-colors ${
                      groupActive
                        ? "text-[var(--af-navy)] bg-[var(--af-lime)]"
                        : "text-[var(--af-lime)]/80 hover:text-[var(--af-lime)]"
                    }`}
                  >
                    {group.label}
                  </button>
                  <div className="flex flex-wrap gap-2 flex-1">
                    {group.members.map((i) => (
                      <button
                        key={i}
                        onClick={() => setIndustry(i)}
                        data-testid={`filter-industry-${i.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded-full border transition-colors ${
                          industry === i
                            ? "bg-[var(--af-lime)] text-[var(--af-navy)] border-[var(--af-lime)] font-semibold"
                            : "border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 hover:border-[var(--af-lime)]"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 lg:max-w-xs lg:flex-shrink-0">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--af-cream)]/60 self-center mr-1">
              Format:
            </span>
            {formats.map((f) => (
              <button
                key={f.key}
                onClick={() => setFormat(f.key)}
                data-testid={`filter-format-${f.key.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded-full border transition-colors ${
                  format === f.key
                    ? "bg-[var(--af-lime)] text-[var(--af-navy)] border-[var(--af-lime)] font-semibold"
                    : "border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 hover:border-[var(--af-lime)]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO ADS GRID */}
      {filteredVideos.length > 0 && (
        <section
          className="px-5 sm:px-8 pt-8 pb-4 sm:pt-16 sm:pb-8"
          data-testid="sample-ads-video-section"
        >
          <div className="max-w-7xl mx-auto">
            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-7 justify-items-center"
              data-testid="sample-ads-video-grid"
            >
              {visibleVideos.map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>

            {remainingVideos > 0 && (
              <div
                className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
                data-testid="sample-ads-video-loadmore-wrap"
              >
                <div className="text-xs uppercase tracking-[0.16em] text-[var(--af-cream)]/55">
                  Showing {visibleVideos.length} of {filteredVideos.length}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setVideoLimit((n) => n + PAGE_SIZE_VIDEOS)
                    }
                    className="btn-lime"
                    data-testid="sample-ads-video-load-more-btn"
                  >
                    Load {Math.min(PAGE_SIZE_VIDEOS, remainingVideos)} more{" "}
                    <Plus size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setVideoLimit(filteredVideos.length)}
                    className="btn-ghost-lime"
                    data-testid="sample-ads-video-show-all-btn"
                  >
                    Show all {filteredVideos.length}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DISPLAY CAMPAIGNS — side-by-side at md+, stacks on mobile */}
      {filteredCampaigns.length > 0 && (
        <section
          className="px-5 sm:px-8 pt-4 pb-10 sm:pt-8 sm:pb-16"
          data-testid="sample-ads-display-section"
        >
          <div className="max-w-7xl mx-auto">
            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-7 justify-items-center"
              data-testid="sample-ads-display-grid"
            >
              {visibleCampaigns.map((c) => (
                <DisplayCampaignCard key={c.id} campaign={c} />
              ))}
            </div>

            {remainingCampaigns > 0 && (
              <div
                className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
                data-testid="sample-ads-display-loadmore-wrap"
              >
                <div className="text-xs uppercase tracking-[0.16em] text-[var(--af-cream)]/55">
                  Showing {visibleCampaigns.length} of{" "}
                  {filteredCampaigns.length}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setDisplayLimit((n) => n + PAGE_SIZE_DISPLAY)
                    }
                    className="btn-lime"
                    data-testid="sample-ads-display-load-more-btn"
                  >
                    Load {Math.min(PAGE_SIZE_DISPLAY, remainingCampaigns)} more{" "}
                    <Plus size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDisplayLimit(filteredCampaigns.length)
                    }
                    className="btn-ghost-lime"
                    data-testid="sample-ads-display-show-all-btn"
                  >
                    Show all {filteredCampaigns.length}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* EMPTY STATE */}
      {isEmpty && (
        <section className="px-5 sm:px-8 py-20">
          <div
            className="max-w-7xl mx-auto text-center text-[var(--af-cream)]/60"
            data-testid="sample-ads-empty"
          >
            No sample ads match that combination yet.
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section
        className="px-5 sm:px-8 py-20 border-t border-[var(--af-line)]"
        data-testid="sample-ads-cta"
      >
        <div className="max-w-5xl mx-auto text-center">
          <div className="eyebrow justify-center inline-flex mb-4">
            Want something like this for your shop?
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-7">
            Your spot could run by{" "}
            <span className="text-[var(--af-lime)]">next month.</span>
          </h2>
          <Link
            to="/home-services"
            className="btn-lime"
            data-testid="sample-ads-cta-btn"
          >
            See the 90-Day Packages <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </SiteShell>
  );
};

export default SampleAds;
